import { useState } from 'react';
import { pizzas, focacce, doughInfo } from '@/data/menu';
import PizzaCard from './PizzaCard';
import { Info, Clock } from 'lucide-react';
import GlutenFreeIcon from '@/components/GlutenFreeIcon';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Link } from 'react-router-dom';
import { useCart } from '@/context/CartContext';

const MenuSection = () => {
  const classiche = pizzas.filter((p) => p.category === 'classiche');
  const specialita = pizzas.filter((p) => p.category === 'specialita');
  const { ordersEnabled } = useCart();

  // Track which pizza has the gluten choice open
  const [openGlutenChoiceId, setOpenGlutenChoiceId] = useState<string | null>(null);

  const handleToggleGlutenChoice = (pizzaId: string, show: boolean) => {
    setOpenGlutenChoiceId(show ? pizzaId : null);
  };

  return (
    <section id="menu" className="py-20 bg-background">
      <div className="container mx-auto px-4">

        {/* Section Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">Il Nostro Menù</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-6">
            Sfoglia il nostro menù e lasciati tentare dal profumo della vera pizza italiana.
          </p>
          
          {/* Gluten-free highlight */}
          <Link to="/aic" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted border border-border hover:border-green-500/40 transition-colors">
            <GlutenFreeIcon className="h-5 w-5 text-green-400" />
            <span className="text-sm text-muted-foreground">Tutte le pizze sono disponibili anche <span className="text-green-400 font-semibold">senza glutine</span></span>
          </Link>
        </div>

        {/* Customization Note - Top (only when orders enabled) */}
        {ordersEnabled &&
        <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/50 border border-border mb-12 max-w-3xl mx-auto">
            <Info className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
            <p className="text-sm text-muted-foreground italic">
              Per rimuovere un ingrediente o aggiungere un supplemento, scrivilo nelle note aggiuntive al momento dell'ordine.
            </p>
          </div>
        }

        {/* Le Classiche */}
        <div className="mb-16">
           <h3 className="text-2xl md:text-3xl font-semibold text-foreground mb-8 text-center">
            Le Classiche
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {classiche.map((pizza) =>
            <PizzaCard
              key={pizza.id}
              pizza={pizza}
              showGlutenChoice={openGlutenChoiceId === pizza.id}
              onToggleGlutenChoice={(show) => handleToggleGlutenChoice(pizza.id, show)}
              ordersDisabled={!ordersEnabled} />

            )}
          </div>
        </div>

        {/* Le Nostre Specialità */}
        <div className="mb-16">
           <h3 className="text-2xl md:text-3xl font-semibold text-foreground mb-8 text-center">
            Le Nostre Specialità
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {specialita.map((pizza) =>
            <PizzaCard
              key={pizza.id}
              pizza={pizza}
              showGlutenChoice={openGlutenChoiceId === pizza.id}
              onToggleGlutenChoice={(show) => handleToggleGlutenChoice(pizza.id, show)}
              ordersDisabled={!ordersEnabled} />

            )}
          </div>
        </div>

        {/* Le Focacce */}
        <div className="mb-16">
          <h3 className="text-2xl md:text-3xl font-semibold text-foreground mb-8 text-center">
            Le Focacce
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {focacce.map((pizza) =>
            <PizzaCard
              key={pizza.id}
              pizza={pizza}
              showGlutenChoice={openGlutenChoiceId === pizza.id}
              onToggleGlutenChoice={(show) => handleToggleGlutenChoice(pizza.id, show)}
              ordersDisabled={!ordersEnabled} />

            )}
          </div>
        </div>

        {/* Dough Info - Bottom */}
        <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/50 border border-border mt-12 max-w-3xl mx-auto">
          <Info className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">Ingredienti impasto:</span> {doughInfo.ingredients}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Supplementi disponibili da €0,50 a €2,00 a seconda dell'ingrediente. Impasto integrale senza supplemento, ai 5 cereali +€1,00.
            </p>
          </div>
        </div>
      </div>
    </section>);

};

export default MenuSection;